import { useMemo, useState } from 'react';
import { IconAlertTriangle, IconBell, IconDots, IconLayoutSidebarRight, IconCircleCheck, IconCheck, IconChevronRight, IconAlarm, IconClockPause, IconInbox, IconList, IconLock, IconPlus, IconSearch, IconSparkles, IconX } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { STAGE_LABEL, STAGE_ORDER, dealsByStage } from './logic';
import { DRAFTS, LOOSE, QUICK, dealAbbr, dealFlags, followLong, followShort, inPic, neighbours, picOptions, searchDeals, sumOpen, sumOverdue, type FlagTone, type FollowTone, type QuickFilter } from './pipeline';
import type { Deal, DealStage } from './types';
import { PipelineCompact } from './PipelineCompact';
import { PipelineDock, usePipelineDock } from './PipelineDock';
import s from './pipeline.module.css';

// Mint-Pipeline (desktop 1536 / 1920): tab Kanban | Danh sách · tìm + PIC + lọc nhanh · nháp Hana + báo giá chưa gắn phiếu · 5 cột giai đoạn.
// Chỉ đọc: mọi nút ghi (xác nhận, nhận thành phiếu, chuyển giai đoạn, tạo phiếu) báo toast "Chưa mở ghi ở bản này".
// Tiền không hiện ở màn này.

const TABS = [
  { key: 'kan', label: 'Kanban' },
  { key: 'list', label: 'Danh sách' },
] as const;
type View = (typeof TABS)[number]['key'];

const DOT: Record<DealStage, string | undefined> = { New: '', Pricing: s.dotPricing, Quoted: s.dotQuoted, Won: s.dotWon, Lost: s.dotLost };
const FLAG: Record<FlagTone, string | undefined> = { ok: s.flagOk, warn: s.flagWarn, err: s.flagErr, pri: s.flagPri };
const FU: Record<FollowTone, string | undefined> = { none: s.fuNone, over: s.fuOver, today: s.fuToday, normal: '' };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function FollowIcon({ d }: { d: Deal }) {
  if (d.followDays === null) return <IconClockPause size={13} stroke={1.8} aria-hidden="true" />;
  return d.followDays < 0 ? <IconAlertTriangle size={13} stroke={1.8} aria-hidden="true" /> : <IconAlarm size={13} stroke={1.8} aria-hidden="true" />;
}

function PipelineDesktop() {
  const t = useT();
  const toast = useToast();
  const dock = usePipelineDock();
  const { data, error, isLoading, isError, refetch } = useList('crmDeal', { limit: 5000 });
  const forced = readState();
  const [view, setView] = useState<View>('kan');
  const [q, setQ] = useState('');
  const [pic, setPic] = useState('__me');
  const [qf, setQf] = useState<QuickFilter | null>(null);

  const all = data?.items ?? [];
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const noperm = state === 'noperm';
  const ready = state === 'ready' || noperm;

  const scope = useMemo(() => inPic(all, noperm ? '__me' : pic), [all, pic, noperm]);
  const rows = useMemo(() => {
    const t = QUICK.find((x) => x.key === qf)?.test;
    return searchDeals(scope, q).filter((d) => !t || t(d));
  }, [scope, q, qf]);
  const groups = useMemo(() => dealsByStage(rows), [rows]);

  const noWrite = () => toast({ msg: t('crm.pb.noWrite') });
  const openDeal = () => toast({ msg: t('crm.pb.noDetail') });

  return (
    <Page kind="table" dock={dock.wide && state === 'ready' ? <PipelineDock rows={all} dock={dock} /> : undefined}>
      <PageHeader
        title={t('crm.at.69')}
        tabs={<Segmented label={t('crm.at.70')} value={view} onChange={setView} items={TABS.map((x) => ({ key: x.key, label: x.key === 'list' ? t('crm.pb.tabList') : x.label }))} />}
        actions={
          <>
            <HeaderTool label={t('crm.at.31')} icon={IconBell} n={2} onClick={() => dock.show('noti')} />
            <HeaderTool label={t('crm.at.71')} icon={IconLayoutSidebarRight} active={dock.open} onClick={() => dock.toggle('stat')} />
            <HeaderTool label={t('crm.at.32')} icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {t('crm.x.createInq')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        {state !== 'loading' && state !== 'error' && (
          <div className={s.tb}>
            <label className={s.search}>
              <IconSearch size={16} aria-hidden="true" />
              <input aria-label={t('crm.at.72')} placeholder={t('crm.at.73')} value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <label className={s.pic}>
              PIC
              <select aria-label={t('crm.at.74')} value={noperm ? '__me' : pic} onChange={(e) => setPic(e.target.value)}>
                {picOptions(all, !noperm).map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.k}
                  </option>
                ))}
              </select>
            </label>
            <div className={s.qfs} role="group" aria-label={t('crm.at.2')}>
              {QUICK.map((f) => (
                <button key={f.key} type="button" className={s.qf} aria-pressed={qf === f.key} onClick={() => setQf(qf === f.key ? null : f.key)}>
                  {f.label} <span>{scope.filter(f.test).length}</span>
                </button>
              ))}
            </div>
            {state === 'ready' && (
              <div className={s.sum}>
                <span title={t('crm.pb.openN', { n: sumOpen(rows) })}>
                  <IconList size={14} stroke={1.8} aria-hidden="true" />
                  {t('crm.pb.openN', { n: sumOpen(rows) })}
                </span>
                <span className={s.hot} title={t('crm.pb.overdueN', { n: sumOverdue(rows) })}>
                  <IconAlertTriangle size={14} stroke={1.8} aria-hidden="true" />
                  {sumOverdue(rows)}
                </span>
              </div>
            )}
          </div>
        )}
        <StateView
          state={ready ? 'ready' : state}
          title={state === 'empty' ? t('crm.pb.emptyT') : undefined}
          desc={state === 'empty' ? t('crm.pb.emptyD') : undefined}
          icon={state === 'empty' ? <IconInbox size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('crm.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={8} />}
        >
          {noperm && (
            <div role="note" className={s.empty}>
              <IconLock size={16} aria-hidden="true" /> {t('crm.x.ownOnly')}
            </div>
          )}
          <div className={s.two}>
            <section className={s.box} aria-label={t('crm.at.75')}>
              <div className={s.boxHead}>
                <span className={s.spark}>
                  <IconSparkles size={14} stroke={1.9} aria-hidden="true" />
                </span>
                <b>{t('crm.pb.drafts')}</b>
                <span className={s.n}>{t('crm.pb.pendingN', { n: DRAFTS.length })}</span>
              </div>
              {DRAFTS.map((d) => (
                <div className={s.line} key={d.id}>
                  <span className={s.src}>{d.src}</span>
                  <div className={s.txt}>
                    <b>{d.customer}</b> · {d.text}
                  </div>
                  <button type="button" className={s.ok} onClick={noWrite}>
                    <IconCircleCheck size={16} stroke={1.8} aria-hidden="true" />
                    {t('crm.x.confirm')}
                  </button>
                  <button type="button" className={s.no} aria-label={t('crm.at.76')} title={t('crm.at.76')} onClick={noWrite}>
                    <IconX size={16} stroke={1.8} aria-hidden="true" />
                  </button>
                </div>
              ))}
            </section>
            <section className={s.box} aria-label={t('crm.at.77')}>
              <div className={s.boxHead}>
                <b>{t('crm.pb.loose')}</b>
                <span className={s.n}>{LOOSE.length}</span>
              </div>
              {LOOSE.map((l) => (
                <div className={s.line} key={l.code}>
                  <span className={s.src}>{l.kind}</span>
                  <div className={s.txt}>
                    <b>{l.code}</b> · {l.customer} · <span className={s.lb}>{l.text}</span>
                  </div>
                  <button type="button" className={s.go} onClick={noWrite}>
                    <IconCheck size={16} stroke={1.8} aria-hidden="true" />
                    {t('crm.x.acceptInq')}
                  </button>
                </div>
              ))}
            </section>
          </div>

          {view === 'kan' ? (
            <section className={s.board} aria-label={t('crm.at.78')}>
              {STAGE_ORDER.map((st) => (
                <div className={s.col} key={st}>
                  <div className={s.colHead}>
                    <span className={`${s.dot} ${DOT[st]}`} aria-hidden="true" />
                    <b>{STAGE_LABEL[st]}</b>
                    <span className={s.n}>{groups[st].length}</span>
                    <span className={s.code}>{st}</span>
                  </div>
                  {groups[st].map((d) => {
                    const fu = followShort(d);
                    const nb = neighbours(d);
                    const closed = st === 'Won' || st === 'Lost';
                    return (
                      <div key={d.id} className={s.card} role="button" tabIndex={0} aria-label={t('crm.pb.openDeal', { id: d.id, c: d.customer })} onClick={openDeal} onKeyDown={(e) => e.key === 'Enter' && openDeal()}>
                        <div className={s.row}>
                          <b>{d.customer}</b>
                          <span className={s.id}>{d.id}</span>
                        </div>
                        <div className={s.subj}>{d.subject}</div>
                        <div className={s.tags}>
                          {d.services.map((v) => (
                            <span className={s.svc} key={v}>
                              {v}
                            </span>
                          ))}
                          {dealFlags(d).map((f) => (
                            <span className={`${s.flag} ${FLAG[f.tone]}`} key={f.text}>
                              {f.text}
                            </span>
                          ))}
                        </div>
                        <div className={s.meta}>
                          <span className={s.sale} title={`Sale ${d.sale}`}>
                            <span className={s.ab} aria-hidden="true">
                              {dealAbbr(d.sale)}
                            </span>
                            {d.sale}
                          </span>
                          <span className={`${s.fu} ${FU[fu.tone]}`} title={followLong(d)}>
                            <FollowIcon d={d} />
                            {fu.text}
                          </span>
                        </div>
                        {closed && <div className={s.closeNote}>{d.closeNote}</div>}
                        {(nb.prev || nb.next) && (
                          <div className={s.steps}>
                            {nb.prev && (
                              <button type="button" className={s.step} aria-label={t('crm.pb.backDeal', { id: d.id, v: nb.prev })} title={t('crm.pc.back', { v: nb.prev })} onClick={(e) => { e.stopPropagation(); noWrite(); }}>
                                ‹
                              </button>
                            )}
                            {nb.next && (
                              <button type="button" className={`${s.step} ${s.stepNext}`} aria-label={t('crm.pb.moveDeal', { id: d.id, v: nb.next })} onClick={(e) => { e.stopPropagation(); noWrite(); }}>
                                {nb.next}
                                <IconChevronRight size={12} stroke={2} aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {st === 'New' && (
                    <button type="button" className={s.add} onClick={noWrite}>
                      <IconPlus size={16} stroke={1.8} aria-hidden="true" />
                      {t('crm.x.createInq')}
                    </button>
                  )}
                </div>
              ))}
            </section>
          ) : (
            <section className={s.tcard} aria-label={t('crm.at.79')}>
              <table className={s.table} aria-label={t('crm.at.80')}>
                <colgroup>
                  {[150, 140, 240, 110, 120, 170, 140, 114, 80].map((w, i) => (
                    <col key={i} style={{ width: `${((w / 1264) * 100).toFixed(3)}%` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {(['deal', 'cust', 'topic', 'svc', 'stage', 'follow', 'quote', 'pricing', 'sale'] as const).map((h) => (
                      <th key={h} scope="col">
                        {t(`crm.pb.th.${h}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => {
                    const closed = d.stage === 'Won' || d.stage === 'Lost';
                    return (
                      <tr key={d.id} className={s.tr} tabIndex={0} onClick={openDeal} onKeyDown={(e) => e.key === 'Enter' && openDeal()}>
                        <td className={s.idc}>{d.id}</td>
                        <td className={s.snc}>{d.customer}</td>
                        <td>{d.subject}</td>
                        <td className={s.dim}>{d.services.join(' · ')}</td>
                        <td>
                          <span className={s.stage}>
                            <span className={`${s.dot} ${DOT[d.stage]}`} aria-hidden="true" />
                            {STAGE_LABEL[d.stage]}
                          </span>
                        </td>
                        <td className={closed ? s.dim : FU[followShort(d).tone]}>{closed ? d.closeNote : followLong(d)}</td>
                        <td className={s.dim}>{d.quotes.join(', ') || '—'}</td>
                        <td className={s.dim}>{d.pricingRequest ? `${d.pricingRequest} · ${d.pricingStatus}` : '—'}</td>
                        <td>{d.sale}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className={s.foot}>{t('crm.pb.foot', { n: rows.length })}</div>
            </section>
          )}
        </StateView>
      </div>
    </Page>
  );
}

/** Máy bàn: Kanban / danh sách (Mint-Pipeline). iPad / điện thoại: Mint-Pipeline-iPad / -Phone. */
export function PipelineBoard() {
  const layout = useLayout();
  return layout === 'pad' || layout === 'phone' ? <PipelineCompact phone={layout === 'phone'} /> : <PipelineDesktop />;
}
